import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'

import RadioGroup from '@mui/material/RadioGroup'

import FormInput from '../inputFields/FormInput'
import MaskedInput from '../inputFields/MaskedInput'
import { theme } from '../../themes/theme'
import { FormRow, FlexColumn } from '../styles/Containers'
import { RowFormControl, StyledFormControlLabel, StyledRadio } from '../styles/RadioGroup'
import { InfoMessage } from '../common/UserMessage'
import BodyText, { FormTitle, ErrorText, BoldSpan } from '../styles/Text'

const Red = styled.span`
  color: ${theme.colors.error};
`

function RequestorForm({ loading = false, variant = 'public' }) {
  const { register, formState, getValues, watch } = useFormContext()
  const [defaultIsRealtor, setDefaultIsRealtor] = useState()

  useEffect(() => {
    const defaultVal = getValues('requestorInformation.isRealtor') !== undefined ? getValues('requestorInformation.isRealtor') : null
    setDefaultIsRealtor(defaultVal)
  }, [])

  const isRealtor = watch('requestorInformation.isRealtor')

  const error = !!formState.errors.requestorInformation?.isRealtor
  const helperText = formState.errors?.requestorInformation?.isRealtor?.message

  return (
    <FlexColumn>
      <FormTitle>Requestor Information</FormTitle>
      {variant !== 'staff' && defaultIsRealtor !== undefined && (
        <FlexColumn gap="0">
          <RowFormControl>
            <BodyText>
              Are you a realtor or working on behalf of a realtor?<Red> *</Red>
            </BodyText>
            <RadioGroup row name="is-realtor-radio-group" id="isRealtor" defaultValue={defaultIsRealtor}>
              <StyledFormControlLabel
                {...register('requestorInformation.isRealtor')}
                value={true}
                control={<StyledRadio />}
                label="Yes"
                disabled={loading}
              />
              <StyledFormControlLabel
                {...register('requestorInformation.isRealtor')}
                value={false}
                control={<StyledRadio />}
                label="No"
                disabled={loading}
              />
            </RadioGroup>
          </RowFormControl>
          {error && <ErrorText role="alert">{helperText}</ErrorText>}
        </FlexColumn>
      )}
      {variant !== 'staff' && (
        <InfoMessage>
          As the person filling out this form, you should fill it out with <BoldSpan>your own information</BoldSpan> even if you are filling it out on
          behalf of a realtor. As the requestor, you will be required to provide your digital signature and will receive a copy of the response files.
          Any additional recipients will also receive a copy of the response files but will <BoldSpan>not</BoldSpan> need to provide their digital
          signature.
        </InfoMessage>
      )}

      <FormRow>
        {variant !== 'staff' && isRealtor && isRealtor.toString() === 'true' && (
          <FormInput id="requestorInformation.brokerageName" label="Agency / Brokerage Name" disabled={loading} />
        )}
        <FormInput required={true} id="requestorInformation.firstName" label="First Name" disabled={loading} />
        <FormInput required={true} id="requestorInformation.lastName" label="Last Name" disabled={loading} />
        <FormInput required={true} id="requestorInformation.address" label="Address" disabled={loading} />
        <MaskedInput required={true} id="requestorInformation.phone" label="Phone" format="(###) ###-####" disabled={loading} />
        <FormInput
          required={true}
          id="requestorInformation.email"
          label="Email"
          disabled={loading}
          tooltip="A digital signature request will be sent to this email to authorize this request."
        />
        {variant !== 'staff' && <FormInput required={true} id="requestorInformation.confirmEmail" label="Confirm Email" disabled={loading} />}
        <FormInput id="requestorInformation.additionalName" label="Additional Name" disabled={loading} />
      </FormRow>
    </FlexColumn>
  )
}

RequestorForm.propTypes = {
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['public', 'staff'])
}

export default RequestorForm
